import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { resourceCategoryLabels, resourceFileTypeLabels } from '@/constants/resource'
import {
  useAdminCreateResource,
  useAdminGetUploadUrl,
  useAdminImportResources,
  useAdminResources,
  useAdminToggleResource,
} from '@/hooks/use-resources'
import { resourceService } from '@/services/resource.service'
import type { ApiError, ResourceBulkImportResponse, ResourceCategory } from '@/types'
import { Download, Library, Loader2, Upload } from 'lucide-react'
import { useState } from 'react'

const CATEGORIES: ResourceCategory[] = [
  'job_search_tracking_tools',
  'personal_branding_and_linkedin_strategy',
  'networking_and_professional_communication',
  'working_with_recruiters',
  'financial_and_career_path_guidance',
  'career_transition_roadmaps_and_wellbeing',
]

export function AdminResourcesPage() {
  const { data, isLoading, refetch } = useAdminResources()
  const createResource = useAdminCreateResource()
  const importResources = useAdminImportResources()
  const getUploadUrl = useAdminGetUploadUrl()
  const toggleResource = useAdminToggleResource()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<ResourceCategory>('job_search_tracking_tools')
  const [sortOrder, setSortOrder] = useState(0)
  const [statusText, setStatusText] = useState<string | null>(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<ResourceBulkImportResponse | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const handleCreate = async () => {
    if (!title.trim()) return
    const result = await createResource.mutateAsync({
      title: title.trim(),
      description: description.trim() || null,
      category,
      sort_order: sortOrder,
    })
    setStatusText(`Created resource #${result.id}. Upload file and cover below.`)
    setTitle('')
    setDescription('')
    setSortOrder(0)
  }

  const handleUpload = async (
    resourceId: number,
    resourceTitle: string,
    uploadType: 'file' | 'cover',
    file: File | null,
  ) => {
    if (!file) return
    const label = resourceTitle.trim() || `resource #${resourceId}`
    const ext = file.name.toLowerCase().split('.').pop() || ''
    if (uploadType === 'file' && !['pdf', 'xls', 'xlsx'].includes(ext)) {
      setStatusText('Only PDF (.pdf) and Excel (.xls, .xlsx) files are accepted.')
      return
    }
    if (uploadType === 'cover' && !['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      setStatusText('Only JPG, PNG, and WEBP images are accepted for cover uploads.')
      return
    }

    try {
      const presigned = await getUploadUrl.mutateAsync({
        resourceId,
        data: {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          upload_type: uploadType,
        },
      })
      await resourceService.uploadToS3(presigned.upload_url, file)
      await refetch()
      setStatusText(`${uploadType === 'file' ? 'File' : 'Cover'} uploaded for ${label}.`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed'
      setStatusText(
        `Failed to upload ${uploadType === 'file' ? 'file' : 'cover'} for ${label}: ${message}`,
      )
    }
  }

  const handleDownloadTemplate = async () => {
    setImportError(null)
    try {
      const blob = await resourceService.adminDownloadResourceTemplate()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'resource_import_template.csv'
      a.click()
      window.URL.revokeObjectURL(url)
      setStatusText('Downloaded current resource metadata as CSV.')
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as ApiError).message)
          : error instanceof Error
            ? error.message
            : 'Download failed'
      setImportError(message)
      setStatusText(`Template download failed: ${message}`)
    }
  }

  const handleImport = async () => {
    if (!csvFile) return
    setImportError(null)
    try {
      const result = await importResources.mutateAsync(csvFile)
      setImportResult(result)
      if (result.errors.length > 0) {
        setStatusText('CSV validation failed. Fix the listed errors and retry.')
        return
      }
      if (result.total_rows === 0) {
        setStatusText(
          'No data rows were found (header only or empty file). Add rows below the header and try again.',
        )
        return
      }
      setStatusText(
        `Import complete: ${result.created} created, ${result.updated} updated, ${result.unchanged} unchanged.`,
      )
    } catch (error) {
      setImportResult(null)
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as ApiError).message)
          : error instanceof Error
            ? error.message
            : 'Import failed'
      setImportError(message)
      setStatusText(`Import failed: ${message}`)
    }
  }

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Resources</h1>
        <p className="text-muted-foreground">
          Create and manage downloadable resource library items.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create Resource</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sort-order">Sort Order</Label>
              <Input
                id="sort-order"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as ResourceCategory)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {resourceCategoryLabels[cat]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            File type is auto-detected from uploaded files. Only PDF (.pdf) and Excel (.xls, .xlsx)
            are accepted. Other file types are rejected.
          </p>
          <Button onClick={handleCreate} disabled={createResource.isPending || !title.trim()}>
            {createResource.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Create
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bulk Metadata Import (CSV)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload metadata only (title, description, category, sort order, active state). File and
            cover uploads remain manual per resource below.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-1" />
              Download Template
            </Button>
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                setCsvFile(e.target.files?.[0] || null)
                setImportResult(null)
                setImportError(null)
              }}
              className="max-w-sm"
            />
            <Button onClick={handleImport} disabled={!csvFile || importResources.isPending}>
              {importResources.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Validate & Import
            </Button>
          </div>
          {importError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {importError}
            </div>
          ) : null}
          {importResult ? (
            <div className="rounded-md border p-3 text-sm space-y-2">
              <div>
                Rows: {importResult.total_rows} | Created: {importResult.created} | Updated:{' '}
                {importResult.updated} | Unchanged: {importResult.unchanged}
              </div>
              {importResult.errors.length > 0 ? (
                <div className="space-y-1 text-red-600">
                  {importResult.errors.map((error, index) => (
                    <div key={`${error.row}-${error.field}-${index}`}>
                      Row {error.row} [{error.field}]: {error.message}
                    </div>
                  ))}
                </div>
              ) : importResult.total_rows === 0 ? (
                <div className="text-amber-800">
                  No data rows to import. Keep the header row and add at least one data row, or
                  export again when resources exist.
                </div>
              ) : (
                <div className="text-green-700">CSV valid and import applied.</div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {statusText ? (
        <div className="rounded-md bg-blue-50 px-4 py-3 text-sm text-blue-700">{statusText}</div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Resource Items</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-6 text-sm text-muted-foreground">Loading resources...</div>
          ) : (
            <div className="space-y-4">
              {(data?.items ?? []).map((item) => (
                <div key={item.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium">{item.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {resourceCategoryLabels[item.category]} -{' '}
                        {resourceFileTypeLabels[item.file_type]}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor={`active-${item.id}`}
                        className="text-xs text-muted-foreground"
                      >
                        Active
                      </Label>
                      <Checkbox
                        id={`active-${item.id}`}
                        checked={item.is_active}
                        onCheckedChange={(checked) =>
                          toggleResource.mutate({ id: item.id, isActive: checked === true })
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={(e) =>
                          handleUpload(item.id, item.title, 'file', e.target.files?.[0] || null)
                        }
                      />
                      <span className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm">
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        Upload File
                      </span>
                    </Label>
                    <Label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        onChange={(e) =>
                          handleUpload(item.id, item.title, 'cover', e.target.files?.[0] || null)
                        }
                      />
                      <span className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm">
                        <Library className="h-3.5 w-3.5 mr-1" />
                        Upload Cover
                      </span>
                    </Label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
